/**
 * The words on the first screens, kept out of the component so a test can hold
 * the rules (no AI or film words, no exclamation marks, no handles, headline
 * under nine words). Sources: well/marketing/02-taglines-and-ads.md (the ad
 * corpus), the verified quotes in src/data/quotes.json, and the research in
 * well/research/01 and 06: promise a doable act today and a thing made, and let
 * the identity arrive as the by-product.
 */

/**
 * Kraben does not introduce himself at all (owner, 2026-09-18) — not in text on
 * the first screen, and not in the clip either. The clip is where he hands over
 * the first exercise in his own voice: "For your first exercise, tell a story.
 * I'll prompt you along the way. Just start talking." Made on the AUX board,
 * frame K29.
 */
export const KRABEN_HELLO = { clip: '/art/kraben-hello.mp4', poster: '/art/kraben-hello.jpg' };

/** The adult door opens on a verified quote (Emerson, public domain), then the question. */
export const DOOR_QUOTE = { text: 'Every artist was first an amateur.', by: 'Ralph Waldo Emerson' };

/** The question stands on its own; no paragraph under it (owner, 2026-09-18). */
export const QUESTION = { title: 'When did you last make something, just because?' };

/**
 * The second question: the creative self the person would like to be. Naming
 * it is the first step to identifying as it (owner, 2026-09-18). More than one
 * is allowed — most people are two or three of these — and the answers are
 * stored on the profile as `dreams`, then handed to the Day 2 exercise, where
 * the badge is written and put up. The copy never uses it as a label yet.
 */
export const DREAM = { title: 'What would you like to be?', body: 'Pick as many as sound like you. You can change them later.' };
export const DREAMS: { id: string; label: string }[] = [
  { id: 'writer', label: 'A writer' },
  { id: 'artist', label: 'An artist' },
  { id: 'filmmaker', label: 'A filmmaker' },
  { id: 'influencer', label: 'An influencer' },
  { id: 'business', label: 'A business owner' },
  { id: 'dreamer', label: 'A dreamer' },
  { id: 'creator', label: 'A creator' },
  { id: 'unsure', label: 'Not sure yet' },
];

/**
 * The idea step: after the dream question, for half of the people the imagine
 * starter brings in (the `idea_step` experiment, lib/experiments.ts,
 * well/product/33 §8). The owner's aim: get their imagination running.
 *
 *  - kind: what kind of thing they want to make. Chips, one tap moves on: this
 *    one should take a second, not a thought.
 *  - about: what it is about. A box and nothing else, no sparks: this is the
 *    one where they think about what they actually want.
 *  - nudge: for "I can't think of one right now", an email so we can help.
 *
 * The crew then draw them inside that idea's world (the backend's idea prompt),
 * in place of a dreamed-up place. Asked about the first dream they picked that
 * is not "Not sure yet" (the road's goal rule, `goalFor` in data/road.ts).
 */
export interface Kind { id: string; label: string }

/** Labels are lowercase with their article: they are dropped into the next title ("your novel"). */
export const KINDS: Record<string, Kind[]> = {
  writer: [
    { id: 'novel', label: 'a novel' },
    { id: 'short-stories', label: 'short stories' },
    { id: 'essays', label: 'essays' },
    { id: 'memoir', label: 'a memoir' },
    { id: 'poems', label: 'poems' },
    { id: 'newsletter', label: 'a newsletter' },
  ],
  artist: [
    { id: 'paintings', label: 'paintings' },
    { id: 'comic', label: 'a comic' },
    { id: 'illustrations', label: 'illustrations' },
    { id: 'sketchbook', label: 'a sketchbook' },
    { id: 'prints', label: 'prints' },
    { id: 'mural', label: 'a mural' },
  ],
  filmmaker: [
    { id: 'short-film', label: 'a short film' },
    { id: 'documentary', label: 'a documentary' },
    { id: 'web-series', label: 'a web series' },
    { id: 'music-video', label: 'a music video' },
    { id: 'feature', label: 'a feature film' },
    { id: 'animation', label: 'an animation' },
  ],
  influencer: [
    { id: 'youtube', label: 'a YouTube channel' },
    { id: 'podcast', label: 'a podcast' },
    { id: 'tiktok', label: 'a TikTok series' },
    { id: 'newsletter', label: 'a newsletter' },
    { id: 'instagram', label: 'an Instagram account' },
    { id: 'blog', label: 'a blog' },
  ],
  creator: [
    { id: 'youtube', label: 'a YouTube channel' },
    { id: 'podcast', label: 'a podcast' },
    { id: 'zine', label: 'a zine' },
    { id: 'newsletter', label: 'a newsletter' },
    { id: 'game', label: 'a game' },
    { id: 'blog', label: 'a blog' },
  ],
  business: [
    { id: 'brand', label: 'a brand' },
    { id: 'shop', label: 'a shop' },
    { id: 'cafe', label: 'a café' },
    { id: 'product', label: 'a product' },
    { id: 'studio', label: 'a studio' },
    { id: 'app', label: 'an app' },
  ],
  dreamer: [
    { id: 'story', label: 'a story' },
    { id: 'world', label: 'a world' },
    { id: 'game', label: 'a game' },
    { id: 'garden', label: 'a garden' },
    { id: 'home', label: 'a home' },
    { id: 'trip', label: 'a trip' },
  ],
};

export const KIND_STEP = {
  title: 'What kind of thing do you want to make?',
  body: 'Go with your gut. You can change it any time.',
  other: 'Something else',
  otherLabel: 'What is it?',
  otherPlaceholder: 'A board game, a song, a quilt…',
  otherDone: 'Next',
  otherBack: 'Back to the list',
  /** the most a typed kind may be, in characters */
  max: 40,
};

/**
 * One faint example per dream for the idea box, so it is never quite blank,
 * but no sparks: the thinking is theirs.
 */
export const ABOUT_PLACEHOLDER: Record<string, string> = {
  writer: 'a lighthouse keeper who hears ships no one else can…',
  artist: 'the people on my bus, drawn as birds…',
  filmmaker: 'two sisters driving their dad’s ashes to the sea…',
  influencer: 'learning to cook everything my grandmother made…',
  creator: 'fixing up old things nobody wanted…',
  business: 'tiny cakes for people who live alone…',
  dreamer: 'a house where every room is a different season…',
};

/**
 * The idea box, asked as the last great idea they had (owner, 2026-10-19).
 * "What do you want it to be about?" asked them to invent one on the spot, and
 * 4 of the 9 W2 visitors who saw it left there; most people have had one and
 * never used it. Nobody has one to hand is a real answer, not a skip, so it is
 * a button the same size as the other, and it goes to the Postcard Machine,
 * where the idea roll deals one with them (components/IdeaRoll.tsx).
 */
export const ABOUT_STEP = {
  title: 'What was your last great idea?',
  label: 'Your last great idea',
  done: 'That was it',
  none: 'I don’t have any',
  /** under the none button: what it does */
  noneHint: 'Then we’ll spin one up together.',
  /** at least this many characters that are not spaces */
  min: 3,
  max: 200,
};

/** "For your novel, or for anything at all. …" */
export function aboutBody(kind: string): string {
  const rest = 'Big or small, used or not. The crew will draw you inside it.';
  return kindNoun(kind) ? `For ${yourKind(kind)}, or for anything at all. ${rest}` : `About anything at all. ${rest}`;
}

/**
 * Which dream the idea step asks about: the first one picked other than "Not
 * sure yet", in the order they were picked (the dream the road leads to). None
 * for "Not sure yet" alone, or no answer at all, and then the step is not shown.
 */
export function dreamForIdea(dreams: string[] | undefined): string | undefined {
  const d = (dreams || []).find((x) => x !== 'unsure');
  return d && Object.prototype.hasOwnProperty.call(KINDS, d) ? d : undefined;
}

/** The kind without its article: "a novel" → "novel", "an Instagram account" → "Instagram account", typed "My comic." → "comic". */
export function kindNoun(kind: string): string {
  return kind.replace(/\s+/g, ' ').trim().replace(/[.!?,;:\s]+$/, '').replace(/^(a|an|the|my|your|some)\s+/i, '').trim();
}

/** "a novel" → "your novel", "short stories" → "your short stories"; nothing to go on → "your idea". */
export function yourKind(kind: string): string {
  const n = kindNoun(kind);
  return n ? `your ${n}` : 'your idea';
}

/** The chip's face: "A novel". */
export function kindChip(label: string): string {
  return label.charAt(0).toUpperCase() + label.slice(1);
}

/** The idea they gave in the intro, when there is one: what kind, and what it is about. */
export function ideaOf(p: { dreamKind?: string; dreamAbout?: string }): { kind: string; about: string } | undefined {
  const about = (p.dreamAbout || '').replace(/\s+/g, ' ').trim().slice(0, ABOUT_STEP.max);
  if (about.replace(/\s/g, '').length < ABOUT_STEP.min) return undefined;
  return { kind: (p.dreamKind || '').replace(/\s+/g, ' ').trim().slice(0, KIND_STEP.max), about };
}

/** The problem, as the person feels it: one tap, then a reply that says what this is for. */
export const WHEN = [
  { id: 'recent', label: 'Recently', reply: 'Good. Then this is a place to keep it going, a few minutes at a time.' },
  { id: 'ages', label: 'Ages ago', reply: 'Lots of people say that. Somewhere along the way, making things got squeezed out by work, phones and the feeling you are not creative. This is the way back in.' },
  { id: 'never', label: 'Never, really', reply: 'Everyone made things once; it just got called playing. Think of this as a chance to play again.' },
];

export interface Panel { art: string | null; pos?: string; title: string; body?: string; rotate?: boolean; quoteBy?: string }

/**
 * The cards after the door. Panel 1 is the offer (small, daily, yours); panel 2
 * is where a small thing goes (the word and the picture swap together); panel 3
 * says what the days are and that the first one is happening right now. It used
 * to be two cards with a paragraph each; the way in was too long, so they are
 * one card and one line of big text (owner, 2026-09-18).
 */
export const PANELS: Panel[] = [
  {
    art: '/art/one-small-thing.webp',
    pos: '50% 55%',
    title: 'Make one small thing a day.',
    body: 'A few minutes of creativity each day, to get you back to your creative self.',
  },
  {
    art: '/art/idea-to-world.webp',
    pos: '50% 50%',
    title: 'Today’s idea is tomorrow’s world.',
    body: 'Some of what you make stays small. Some of it grows into the next thing, and the next.',
    rotate: true,
  },
  {
    art: '/art/your-world.webp',
    pos: '50% 62%',
    title: 'One exercise a day. The first starts now.',
  },
];

/** Where a small idea can go. The word and the picture swap together. */
export const BECOMES: { word: string; art: string }[] = [
  { word: 'world', art: '/art/idea-to-world.webp' },
  { word: 'story', art: '/art/idea-to-story.jpg' },
  { word: 'film', art: '/art/idea-to-film.jpg' },
  { word: 'novel', art: '/art/idea-to-novel.jpg' },
  { word: 'business', art: '/art/idea-to-business.jpg' },
];

/** Words the brief keeps off these screens (STYLE-K copy rules + the intro rule about AI). */
export const BANNED_WORDS = ['journey', 'unlock', 'unleash', 'manifest', 'self-care', 'healing', 'content', 'creator', 'level up', 'generate', 'prompt', 'output', 'AI'];

/** The labels for what the person said they would like to be, in the order they picked. */
export function dreamLabels(ids: string[] | undefined): string[] {
  return (ids || []).map((id) => DREAMS.find((d) => d.id === id)?.label).filter((l): l is string => !!l && l !== 'Not sure yet');
}

/** Those answers as one line for the badge on Day 2: "A writer and an artist". */
export function dreamPhrase(ids: string[] | undefined): string {
  const l = dreamLabels(ids);
  if (l.length === 0) return '';
  const rest = l.slice(1).map((x) => x.charAt(0).toLowerCase() + x.slice(1));
  if (rest.length === 0) return l[0];
  return `${[l[0], ...rest.slice(0, -1)].join(', ')} and ${rest[rest.length - 1]}`;
}
